import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "@/lib/posthog";
import { Button } from "./button";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("ErrorBoundary caught:", error, errorInfo);
		captureException(error, {
			componentStack: errorInfo.componentStack,
			source: "ErrorBoundary",
		});
	}

	handleReset = () => {
		this.setState({ hasError: false, error: null });
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<div className="flex min-h-screen flex-col items-center justify-center bg-muted px-4">
					<div className="w-full max-w-md text-center">
						<h1 className="mb-2 text-2xl font-bold text-foreground">
							Something went wrong
						</h1>
						<p className="mb-6 text-muted-foreground">
							An unexpected error occurred. Please try again.
						</p>
						{this.state.error && (
							<pre className="mb-6 max-h-32 overflow-auto rounded-md bg-card p-4 text-left text-xs text-muted-foreground">
								{this.state.error.message}
							</pre>
						)}
						<div className="flex gap-3 justify-center">
							<Button variant="outline" onClick={this.handleReset}>
								Try Again
							</Button>
							<Button
								variant="primary"
								onClick={() => {
									window.location.href = "/dashboard";
								}}
							>
								Go to Dashboard
							</Button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

export { ErrorBoundary };
